import { useEffect, useMemo, useState } from "react";
import type { ForexAccount, ForexConfig, ForexPair } from "../api/dataSource";
import { TradingViewEmbed } from "../components/TradingViewEmbed";
import { assetClassOf } from "../lib/assetClass";
import { fmtUSD } from "../lib/format";
import type { Account, EventRow, Pending } from "../types";

// ForexScreen (Phase 9-UI).
//
// Deliberate differences from the CryptoScreen precedent:
//   * Data flows through the DataSource hook (props), never a raw
//     ApiClient — fixtures live in fixtureDataSource.ts only, and
//     tests stub plain objects.
//   * No screen-owned polling. The gate flag and pair reference rows
//     are near-static; they refresh with the app-level ds.refresh().
//     Only the session clock ticks locally.
//   * Most panels are honest "Phase 9b/9c pending" states today:
//     no broker adapter, calendar, margin model, or strategies exist
//     yet. They light up as the backend phases land.

type FxSession = {
  open: boolean;
  label: string;
};

// Client-side 24/5 approximation: open Sunday 17:00 ET through
// Friday 17:00 ET. Labeled "approx" everywhere it renders — the
// authoritative ForexCalendar arrives with Phase 9b (CF-9a-hours-1).
const fxSessionInfo = (now: Date): FxSession => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    hour12: false,
    hour: "2-digit",
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const rawHour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const hour = rawHour === 24 ? 0 : rawHour;
  let open: boolean;
  if (weekday === "Sat") open = false;
  else if (weekday === "Sun") open = hour >= 17;
  else if (weekday === "Fri") open = hour < 17;
  else open = true;
  return {
    open,
    label: open
      ? "OPEN · closes Fri 17:00 ET"
      : "CLOSED · opens Sun 17:00 ET",
  };
};

const utcClock = (now: Date): string => now.toISOString().slice(11, 19);

// "0.02" → "2% (50:1)". Display-only; no money math (TDD §15.1).
const marginLabel = (marginRequirementPct: string): string => {
  const pct = Number(marginRequirementPct);
  if (!Number.isFinite(pct) || pct <= 0) return marginRequirementPct;
  const leverage = Math.round(1 / pct);
  return `${(pct * 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}% (${leverage}:1)`;
};

const splitPair = (symbol: string): { base: string; quote: string } => {
  const [base, quote] = symbol.split("/");
  return { base: base ?? symbol, quote: quote ?? "" };
};

// TradingView chart-feed symbol. "FX:" is TradingView's FXCM-sourced
// feed — chart data only, independent of the actual broker (tastyfx).
const tvSymbolFor = (pair: string): string =>
  `FX:${pair.replace("/", "")}`;

type ReadinessTone = "ok" | "warn" | "idle";

export const ForexScreen = ({
  accounts,
  pending,
  recentEvents,
  forexConfig,
  forexPairs,
  forexAccount = null,
  onOpenApproval,
}: {
  accounts: Account[];
  pending: Pending[];
  recentEvents: EventRow[];
  forexConfig: ForexConfig | null;
  forexPairs: ForexPair[];
  // Phase 9 setup: tastyfx demo-row registration state. The row is
  // DISABLED pre-9b, so it never appears in `accounts` (the registry
  // filters enabled=TRUE) — this prop is the only way to know it exists.
  forexAccount?: ForexAccount | null;
  onOpenApproval: (approvalId: string) => void;
}) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const session = fxSessionInfo(now);

  const gateEnabled = forexConfig?.forexTradingEnabled === true;
  const gateKnown = forexConfig !== null;

  const forexAccounts = useMemo(
    () => accounts.filter((a) => a.assetClass === "forex"),
    [accounts],
  );
  // Explicit-field classification ONLY: there is deliberately no
  // forex symbol heuristic (see lib/assetClass.ts), so rows appear
  // here exactly when the server classified them.
  const forexPending = useMemo(
    () => pending.filter((p) => assetClassOf(p) === "forex"),
    [pending],
  );
  const forexEvents = useMemo(
    () =>
      recentEvents.filter((e) =>
        `${e.type} ${e.summary}`.toLowerCase().includes("forex"),
      ),
    [recentEvents],
  );

  const [chartPair, setChartPair] = useState<string>("EUR/USD");
  useEffect(() => {
    if (
      forexPairs.length > 0 &&
      !forexPairs.some((p) => p.symbol === chartPair)
    ) {
      setChartPair(forexPairs[0].symbol);
    }
  }, [forexPairs, chartPair]);

  const readiness: Array<{
    label: string;
    status: string;
    tone: ReadinessTone;
    detail: string;
  }> = [
    {
      label: "Data entitlement",
      status: "Confirmed",
      tone: "ok",
      detail:
        "Polygon forex aggs (C: prefix) verified against the live API 2026-08-13.",
    },
    {
      label: "Broker adapter (tastyfx)",
      status: "Phase 9b",
      tone: "idle",
      detail:
        "Not built. tastyfx (IG US) is the primary broker — operator " +
        "account exists; OANDA can be added later as a second venue.",
    },
    {
      label: "Calendar / scheduler",
      status: "Phase 9b",
      tone: "idle",
      detail: "Session clock on this screen is a client-side approximation.",
    },
    {
      label: "Margin / leverage model",
      status: "Phase 9b",
      tone: "idle",
      detail: "Forex shorts stay watch-only until this lands.",
    },
    {
      label: "Strategies",
      status: "Phase 9c",
      tone: "idle",
      detail: "No forex strategies registered yet.",
    },
    {
      label: "Trading gate",
      status: gateKnown ? (gateEnabled ? "Enabled" : "Disabled") : "Unknown",
      tone: gateEnabled ? "ok" : "warn",
      detail:
        "system_config.forex_trading_enabled — Override 5 Tier A; " +
        "enforced at the token-minting layer.",
    },
  ];

  return (
    <div data-screen-label="Forex">
      <div className="tp-page-head">
        <h1>Forex</h1>
        <div className="cx-clocks">
          <span className={`fx-session-chip ${session.open ? "ok" : "idle"}`}>
            <span className="dot" />
            FX session {session.label}
            <span className="approx">approx</span>
          </span>
          <span>
            <span className="lbl">UTC</span>
            {utcClock(now)}
          </span>
        </div>
      </div>

      {gateKnown && !gateEnabled && (
        <div className="tp-banner warn" role="status" aria-live="polite">
          <span className="icon">⚠</span>
          <div>
            Forex trading is disabled (Override 5 Tier A gate). Signals can
            be generated and reviewed; order confirmation is blocked at the
            token-minting layer. Enabling requires Phase 9e paper-trade
            validation and operator sign-off.
          </div>
        </div>
      )}

      <div className="tp-acct-row" style={{ marginBottom: 14, marginTop: 12 }}>
        {forexAccounts.length === 0 && forexAccount?.registered ? (
          <div className="tp-pending-live-card">
            <div className="tp-pending-live-head">
              <span className="tp-pending-live-name">
                {forexAccount.displayName ?? "tastyfx demo"}
              </span>
              <span className="tp-pending-live-badge">
                Registered — dormant until Phase 9b
              </span>
            </div>
            <div className="tp-pending-live-meta">
              tastyfx · demo ·{" "}
              <span className="num">
                ···{forexAccount.externalIdLast4 ?? ""}
              </span>
            </div>
            <p className="tp-pending-live-note">
              The demo account row exists but stays disabled — invisible
              to the broker registry — until the Phase 9b adapter lands.
              Credentials are stored in the OS keychain via Config →
              Data. Nothing can trade from this row today.
            </p>
          </div>
        ) : forexAccounts.length === 0 ? (
          <div className="tp-pending-live-card">
            <div className="tp-pending-live-head">
              <span className="tp-pending-live-name">tastyfx</span>
              <span className="tp-pending-live-badge">
                Not provisioned — Phase 9b
              </span>
            </div>
            <div className="tp-pending-live-meta">
              forex · demo account setup available in Config → Data
            </div>
            <p className="tp-pending-live-note">
              No forex broker account is registered yet. Store tastyfx
              credentials and register the demo account under Config →
              Data (safe: the row stays disabled until the Phase 9b
              adapter). OANDA can be added later. The readiness panel
              below tracks what is built versus pending.
            </p>
          </div>
        ) : (
          forexAccounts.map((account) => (
            <div
              key={account.brokerAccountId}
              className={`tp-acct-card ${account.kind}`}
            >
              <div className="head">
                <span className={`tp-acct-tag ${account.kind}`}>
                  {account.label}
                </span>
                <span className="broker num">{account.broker}</span>
              </div>
              <div className="equity num">{fmtUSD(account.equity)}</div>
              <div className="meta">
                <span className={account.todayPL >= 0 ? "pos" : "neg"}>
                  {fmtUSD(account.todayPL)} today
                </span>
              </div>
              <div className="sub">
                {account.balanceSource === "asset_class_sleeve" ||
                account.balanceSource === "asset_class_exposure"
                  ? `forex sleeve · broker ${fmtUSD(account.brokerEquity ?? account.equity)}`
                  : `status ${account.status}`}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="tp-forex-grid">
        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Phase readiness</span>
            <span className="tp-muted">Phase 9a foundation shipped</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {readiness.map((row) => (
              <div className="fx-readiness-row" key={row.label}>
                <span className="name">{row.label}</span>
                <span className={`tp-status ${row.tone}`}>
                  <span className="dot" />
                  {row.status}
                </span>
                <span className="detail">{row.detail}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Pairs ({forexPairs.length})</span>
            <span className="tp-muted">USD-quoted majors · v1 scope</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {forexPairs.length === 0 ? (
              <div className="tp-empty-state">
                No pair reference data. Run migrations (0099 seeds the 7
                majors), then refresh.
              </div>
            ) : (
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th className="right">Pip</th>
                    <th className="right">Dp</th>
                    <th className="right">Min unit</th>
                    <th className="right">Margin</th>
                    <th className="center">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {forexPairs.map((pair) => {
                    const parts = splitPair(pair.symbol);
                    return (
                      <tr key={pair.symbol}>
                        <td>
                          <span className="fx-pair">
                            {parts.base}
                            <span className="quote">/{parts.quote}</span>
                          </span>
                        </td>
                        <td className="num right">{pair.pipSize}</td>
                        <td className="num right">
                          {pair.priceDecimalPlaces}
                        </td>
                        <td className="num right">{pair.minUnitSize}</td>
                        <td className="num right">
                          {marginLabel(pair.marginRequirementPct)}
                        </td>
                        <td className="center">
                          <span
                            className={`tp-status tp-status-compact ${pair.isActive ? "ok" : "idle"}`}
                          >
                            <span className="dot" />
                            {pair.isActive ? "yes" : "no"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="tp-tile" style={{ gridColumn: "1 / -1" }}>
          <div className="tp-tile-head">
            <span className="label">Chart</span>
            <span className="tp-muted">
              External TradingView data — native charts arrive with Phase 9b
              ingest
            </span>
          </div>
          <div className="tp-tile-body">
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <label className="tp-muted" htmlFor="fx-chart-pair">
                Pair
              </label>
              <select
                id="fx-chart-pair"
                className="tp-input"
                style={{ width: 140 }}
                value={chartPair}
                onChange={(e) => setChartPair(e.target.value)}
              >
                {(forexPairs.length > 0
                  ? forexPairs.map((p) => p.symbol)
                  : ["EUR/USD"]
                ).map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </select>
              <span className="tp-muted num">{tvSymbolFor(chartPair)}</span>
            </div>
            <TradingViewEmbed symbol={tvSymbolFor(chartPair)} />
          </div>
        </section>

        <section className="tp-tile" style={{ gridColumn: "1 / -1" }}>
          <div className="tp-tile-head">
            <span className="label">Signal workbench</span>
            <span className="tp-muted">Phase 9c</span>
          </div>
          <div className="tp-tile-body">
            <div className="tp-config-unavailable">
              <div className="title">No forex strategies registered</div>
              <div className="detail">
                Signal generation for forex arrives with Phase 9c (starter
                strategies + /api/v1/forex/signals/generate). Candidates
                will appear here with queue/watch-only gate status; short
                setups stay watch-only until the Phase 9b margin model
                lands.
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                disabled
                title="No forex strategies are registered yet (Phase 9c)."
              >
                Run forex signals
              </button>
            </div>
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">
              Pending approvals ({forexPending.length})
            </span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {forexPending.length === 0 ? (
              <div className="tp-empty-state">
                No forex approvals pending.
              </div>
            ) : (
              forexPending.map((approval) => {
                const parts = splitPair(approval.sym);
                const delta = approval.ai - approval.det;
                const diverge = Math.abs(delta) > 20;
                return (
                  // Reuses the crypto approval-card styling (cx-approval)
                  // with an fx- hook class; the card anatomy is shared.
                  <div
                    className="cx-approval fx-approval"
                    key={approval.approvalId}
                  >
                    <div className="top">
                      <span className="pair">
                        {parts.base}
                        <span className="quote">/{parts.quote}</span>
                      </span>
                      <span className="cx-inst spot">fx</span>
                      <span className="cx-side long">{approval.side}</span>
                      <span className="strat">· {approval.strategy}</span>
                      <span className="grow" />
                      <span className="tp-status warn">manual</span>
                    </div>
                    <div className="plan">
                      {approval.entry}
                      <span style={{ color: "var(--tp-fg-4)" }}> · stop </span>
                      {approval.stop}
                      <span style={{ color: "var(--tp-fg-4)" }}> · size </span>
                      {approval.qty.toLocaleString("en-US")}
                      <span style={{ color: "var(--tp-fg-4)" }}> · risk </span>
                      <span className="risk">{fmtUSD(approval.risk)}</span>
                    </div>
                    <div className="tp-ai-block" style={{ marginTop: 6 }}>
                      <div className="head">
                        <span>AI rationale</span>
                        <span>conf {approval.ai}</span>
                        <span>· det {approval.det}</span>
                        <span className={diverge ? "neg" : "pos"}>
                          Δ{delta}
                        </span>
                      </div>
                      <div className="quote">"{approval.reason}"</div>
                      <div className="cited">{approval.cited}</div>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="tp-btn tp-btn-primary"
                        onClick={() => onOpenApproval(approval.approvalId)}
                      >
                        Review
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Forex events</span>
            <span className="tp-tile-meta">{forexEvents.length}</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            {forexEvents.length === 0 ? (
              <div className="tp-empty-state">No recent forex events.</div>
            ) : (
              forexEvents.slice(0, 8).map((event, index) => (
                <div className="fx-event-row" key={`${event.time}-${index}`}>
                  <span className="type">{event.type}</span>
                  <span className="meta">{event.time}</span>
                  <span>{event.summary}</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
